// src/features/patient/care-treatment/components/careStyles.ts

/**
 * Purpose : The Care & Treatment look in one place. Every class here is a
 * literal the platform already uses (Care & Treatment, the clinical note
 * page, GoldenRecord, the brand button); none is new. New screens use these
 * so they match their neighbours exactly.
 *
 * Contents :
 *
 * * const(pageClass) ... const(brandButtonClass) : shared class strings
 */

// Page and cards
export const pageClass = "min-h-full bg-[#F7F7F7] p-4 sm:p-6";
export const cardClass = "rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm";
export const clickableCardClass = "cursor-pointer rounded-3xl border-[#E5E7EB] bg-white shadow-sm transition-all hover:bg-[#FFF8F8] hover:shadow-md";
/** The same card, not clickable (a visit with nothing more to open). */
export const staticCardClass = "rounded-3xl border-[#E5E7EB] bg-white shadow-sm";
export const statCardClass = "!flex !h-auto !min-h-[110px] !cursor-pointer !flex-col !items-stretch !justify-between !rounded-3xl !border-[#E5E7EB] !bg-white !px-4 !py-5 !text-left !shadow-sm hover:!-translate-y-0.5 hover:!border-[#E5E7EB] hover:!bg-white hover:!shadow-lg 2xl:!px-6";
/** A row inside a card that opens something (the visit page's "Notes from this visit"). */
export const rowButtonClass = "!flex !h-auto !items-center !justify-between !rounded-2xl !border-[#E5E7EB] !bg-white !px-4 !py-3 !text-left hover:!bg-[#FFF8F8]";
export const factBoxClass = "rounded-2xl bg-[#F3F4F6] p-4";
export const dividerClass = "border-t border-[#E5E7EB]";

// Icon tile and tags
export const redIconBoxClass = "flex shrink-0 items-center justify-center rounded-2xl bg-[#E4002B]/50";
export const concernLabelClass = "rounded-md bg-[#E4002B]/50 px-2 py-0.5 text-[11px] font-semibold !text-white";
/** Grey chip: a note's section names / note types. */
export const greyChipClass = "rounded-full bg-[#F3F4F6] px-2 py-0.5 text-[11px] font-semibold text-[#6B7280]";
/** Navy chip: "Visit summary", links to the patient's own records, source badges. */
export const navyChipClass = "rounded-full bg-[#EEF2F7] px-2 py-0.5 text-[11px] font-semibold text-[#0A2947]";
export const navyLinkChipClass = "inline-flex items-center gap-1 rounded-full bg-[#EEF2F7] px-2 py-0.5 text-[11px] font-semibold text-[#0A2947] hover:bg-[#E2E8F0]";
/** "From a document, not yet confirmed" tone (GoldenRecord), for a link we only inferred. */
export const inferredNoticeClass = "rounded-xl bg-[#FEF6DC] px-3 py-2 text-xs font-semibold text-[#946200]";

// Banner and filter chips
export const warningClass = "mb-4 rounded-2xl border border-[#E5E7EB] bg-[#F3F4F6] p-4 text-sm font-medium text-[#6B7280]";
export const filterButtonClass = "!h-7 !rounded-full !px-3 !text-[10px] !font-bold";
export const activeFilterClass = "!border-[#F36F8F] !bg-[#F36F8F] !text-white hover:!border-[#F36F8F] hover:!bg-[#F36F8F] hover:!text-white";
export const inactiveFilterClass = "!border-[#E5E7EB] !bg-[#F3F4F6] !text-[#111827] hover:!border-[#D1D5DB] hover:!bg-white hover:!text-[#111827]";

// Text
export const titleClass = "font-extrabold text-[#0A2947]";
export const subTextClass = "text-sm text-[#6B7280]";
export const faintTextClass = "text-xs text-[#9CA3AF]";
export const labelTextClass = "text-sm text-[#9CA3AF]";
export const bodyTextClass = "text-sm text-[#111827]";
export const sectionHeadingClass = "text-lg font-extrabold text-[#0A2947]";
/** A red text link on a plain <button>. "!" because antd's reset.css (unlayered) makes buttons
 *  inherit the surrounding colour and size, which beats Tailwind's layered classes. */
export const textLinkClass = "!text-xs !font-bold !text-[#E4002B]/80 hover:!text-[#E4002B] hover:underline";
export const mutedIconClass = "h-12 w-12 text-[#9CA3AF]";

// Buttons (the platform's brand button)
export const outlineButtonClass = "!rounded-full !border-[#E5E7EB] !text-sm !font-semibold !text-[#0A2947]";
export const brandButtonClass = "!h-auto !rounded-lg !border-transparent !bg-[#E4002B]/50 !px-4 !py-2 !text-sm !font-semibold !text-white hover:!border-transparent hover:!bg-[#E4002B]/60 hover:!text-white";
